import { getDbSafe, getAuthSafe } from './firebaseConfig';
import {
    collection, doc, getDoc, getDocs, query, where, setDoc, deleteDoc,
    serverTimestamp, arrayUnion, Timestamp
} from 'firebase/firestore';

/**
 * "Recently interacted" hides for the World Yap tab.
 *
 * Once a reader likes, shares, replies to or shouts a yap, that yap yields its
 * place in their World feed to untouched yaps for two days, so refreshing leads
 * with something new rather than the same posts they've already acted on.
 *
 * "Hide" is historical: the feed demotes these by a fixed number of slots (see
 * SINK_DEMOTION_SLOTS in YapTabScreen) and never removes or floors them.
 *
 * Storage is a per-user subcollection, users/{uid}/feed_hides/{yapId}:
 *
 *   { at, expiresAt, sources: ['like', 'reply', ...] }
 *
 * The document id is the yap id, which buys two things. Re-interacting simply
 * overwrites the doc and re-arms the window (no duplicate rows to reconcile),
 * and a hide can be cleared by id without a query.
 *
 * `at` is the correctness mechanism: reads filter on it, so a doc that outlives
 * its window is inert whether or not anything ever deletes it. `expiresAt`
 * exists purely so a Firestore TTL policy can garbage-collect the rows. TTL
 * deletion is asynchronous and can lag up to 24h, so it must never be relied on
 * for correctness — see docs/feed-hides-ttl.md.
 *
 * Scope: the World tab only. Group posts (forum / tribe) are never recorded,
 * because they surface in their own feeds which this does not touch.
 */

export type FeedHideSource = 'like' | 'share' | 'reply' | 'shout' | 'vote';

/** How long an interaction keeps a yap out of the reader's World feed. */
export const FEED_HIDE_WINDOW_MS = 48 * 60 * 60 * 1000;

/** Only world-feed collections can produce a world-feed hide. */
const HIDEABLE_COLLECTIONS = new Set(['yaps', 'shouts']);

/**
 * Collapses a post id onto the yap the World feed actually renders.
 *
 * A shout and the yap it quotes are two documents but one piece of content as
 * far as the reader is concerned, so they must share a hide key — otherwise
 * liking the shout leaves the original still rotating through the feed.
 */
const normalizeKey = (postId: string, collectionName: string, postData?: any): string => {
    if (collectionName === 'shouts') {
        const original = postData?.originalYapId || postData?.originalYap?.id;
        if (original) return String(original);
    }
    return String(postId);
};

export const FeedHideService = {
    normalizeKey,

    /** True when a post in this collection is eligible to be hidden. */
    isHideable: (collectionName?: string | null) =>
        !!collectionName && HIDEABLE_COLLECTIONS.has(collectionName),

    /**
     * Record (or re-arm) a hide. Best-effort by design: a failure here must never
     * take down the like / reply / share it accompanies, which is why every call
     * site fires this without awaiting a result it depends on.
     */
    recordHide: async (
        postId: string,
        source: FeedHideSource,
        collectionName: string,
        postData?: any,
    ): Promise<void> => {
        try {
            if (!FeedHideService.isHideable(collectionName)) return;
            const auth = await getAuthSafe();
            if (!auth.currentUser) return;
            const db = await getDbSafe();

            const key = normalizeKey(postId, collectionName, postData);
            if (!key) return;

            await setDoc(
                doc(db, 'users', auth.currentUser.uid, 'feed_hides', key),
                {
                    at: serverTimestamp(),
                    expiresAt: Timestamp.fromMillis(Date.now() + FEED_HIDE_WINDOW_MS),
                    // Tracked as a set so a yap hidden by several actions survives
                    // undoing just one of them (unliking something you also replied
                    // to must not put it back in rotation).
                    sources: arrayUnion(source),
                },
                { merge: true },
            );
            console.info(`[FeedHideService] hid ${key} (${source})`);
        } catch (e: any) {
            // Loud, and with the Firestore error code. This used to warn quietly,
            // which is how likes silently stopped recording hides while votes kept
            // working — the feature looked broken with nothing to diagnose from.
            console.error(
                `[FeedHideService] recordHide FAILED source=${source} collection=${collectionName} ` +
                `code=${e?.code || 'none'} msg=${e?.message || e}`,
            );
        }
    },

    /**
     * Drop one source from a hide, deleting the row only once no source is left.
     *
     * Undoing an action the reader took by accident has to give the yap back —
     * burying content for two days with no recovery path would be a trap. But an
     * unlike must not clear a hide that a reply also earned, hence the set.
     */
    clearHide: async (
        postId: string,
        source: FeedHideSource,
        collectionName: string,
        postData?: any,
    ): Promise<void> => {
        try {
            if (!FeedHideService.isHideable(collectionName)) return;
            const auth = await getAuthSafe();
            if (!auth.currentUser) return;
            const db = await getDbSafe();

            const key = normalizeKey(postId, collectionName, postData);
            if (!key) return;

            const ref = doc(db, 'users', auth.currentUser.uid, 'feed_hides', key);
            const snap = await getDoc(ref);
            if (!snap.exists()) return;

            const remaining = ((snap.data()?.sources as string[]) || []).filter((s) => s !== source);
            if (remaining.length === 0) {
                await deleteDoc(ref);
            } else {
                await setDoc(ref, { sources: remaining }, { merge: true });
            }
        } catch (e) {
            console.warn('[FeedHideService] clearHide failed:', e);
        }
    },

    /**
     * Every hide still inside its window, as yapId → interaction time (ms).
     *
     * One query per feed load. The timestamp rides along for callers that want
     * to order by how recently the reader touched each yap.
     */
    fetchActiveHides: async (): Promise<Map<string, number>> => {
        const result = new Map<string, number>();
        try {
            const auth = await getAuthSafe();
            if (!auth.currentUser) return result;
            const db = await getDbSafe();

            // Filtering on `at` at read time is what makes expiry work; nothing
            // needs to have pruned the expired rows first.
            const cutoff = Timestamp.fromMillis(Date.now() - FEED_HIDE_WINDOW_MS);
            const snap = await getDocs(query(
                collection(db, 'users', auth.currentUser.uid, 'feed_hides'),
                where('at', '>', cutoff),
            ));

            snap.docs.forEach((d) => {
                const at = (d.data()?.at as any)?.toDate?.();
                result.set(d.id, at ? at.getTime() : 0);
            });
        } catch (e) {
            console.warn('[FeedHideService] fetchActiveHides failed:', e);
        }
        return result;
    },
};
